import { computed, ref, watch } from 'vue';

import { useDynamicYieldCampaigns } from '~/composables/use-dynamic-yield-campaigns';
import {
  CAMPAIGN_STATUS,
  ENGAGEMENT_TYPES,
  RECOMMENDATIONS_CAMPAIGNS,
} from '~/constants/campaigns';
import * as DataDogLogs from '~/lib/datadog/datadog';

/**
 * Composable for managing product recommendations
 * @param {string} templateName - The name of the current template
 * @returns {Object} Recommendations state and methods
 */
export function useRecommendations(templateName) {
  const { campaigns, reportCampaignEngagement, transformCampaignData } = useDynamicYieldCampaigns();

  const isLoading = ref(true);
  const hasError = ref(false);
  const recommendations = ref([]);
  const errorMessage = ref('');
  const selector = RECOMMENDATIONS_CAMPAIGNS[templateName?.toLowerCase()];

  if (!selector) {
    DataDogLogs.error(
      '[Allbirds composable] - useRecommendations - No campaign found for this template',
      { templateName },
      new Error('No campaign found for this template'),
    );

    hasError.value = true;

    return { hasError };
  }

  // Computed properties
  const hasRecommendations = computed(() => recommendations.value.length > 0);
  const displayOptions = computed(() => {
    const firstItem = recommendations.value[0]?.custom || {};
    return {
      showColorway: firstItem.showColorway !== 'false',
      showPrice: firstItem.showPrice !== 'false',
      title: firstItem.title || '',
    };
  });

  const transformRecommendations = (variationData) => {
    const items = variationData?.payload?.data?.slots || [];
    return items.map((item) => {
      const productData = item?.productData || {};
      return {
        bestFor: productData.best_for?.split('|').slice(0, 3) || '',
        colorName: (productData.display_color || productData.color || '')
          .replaceAll('\\', ' ')
          .replaceAll('/', '/ '),
        custom: variationData?.payload?.data?.custom || {},
        image: productData.featured_image_v2 ? `${productData.featured_image_v2}?w=1024` : '',
        name: productData.name || productData.title || '',
        price: productData.dy_display_price
          ? `$${productData.dy_display_price}`
          : productData.price
            ? `$${productData.price}`
            : '',
        salePrice: productData.sale_price ? `$${productData.sale_price}` : '',
        sku: item.sku || productData.sku,
        slotId: item.slotId || '',
        url: productData.url || '',
      };
    });
  };

  const handleRecommendationsCampaign = (campaign) => {
    if (!Object.keys(campaign).length || !campaign.enabled) return;

    const transformedRecommendations = transformCampaignData(campaign, 0, transformRecommendations);
    recommendations.value = transformedRecommendations;

    // Report impressions with section context
    const slotIds = recommendations.value.map((item) => item.slotId);
    reportCampaignEngagement({
      campaignName: campaign.selectorName,
      engagementType: ENGAGEMENT_TYPES.SLOT_IMPRESSION,
      slotIds,
    });
  };

  const reportRecommendationClick = async (item, event) => {
    event.preventDefault();
    if (!item || Object.keys(item).length === 0) return;

    try {
      await reportCampaignEngagement({
        campaignName: selector,
        engagementType: ENGAGEMENT_TYPES.SLOT_CLICK,
        slotId: item.slotId,
      });
      window.location.href = item.url;
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useRecommendations - reportRecommendationClick - Error reporting recommendation click',
        { item, selector },
        error,
      );
      window.location.href = item.url;
    }
  };

  // Watch for campaign changes
  watch(
    campaigns,
    (newCampaigns) => {
      isLoading.value = false;
      const campaign = newCampaigns?.global[selector] || {};

      if (!campaign || !Object.keys(newCampaigns).length || !Object.keys(campaign).length) return;

      if (campaign.status === CAMPAIGN_STATUS.ERROR) {
        hasError.value = true;
        errorMessage.value = campaign.error || 'Failed to load recommendations';
        return;
      }

      handleRecommendationsCampaign(campaign);
    },
    { deep: true },
  );

  return {
    displayOptions,
    errorMessage,
    hasError,
    hasRecommendations,
    isLoading,
    recommendations,
    reportRecommendationClick,
  };
}
